import Phaser from 'phaser';
import {
  MAX_WAVES,
  MAX_WAVE_ENTRIES,
  metEnemyTypes,
  type CreatorProject,
  type CreatorWave,
} from '../../config/creator';
import { HUB_MENU_TRACK } from '../../config/levels';
import { creatorStoreNow } from '../creator/storeAccess';
import { ScrollList } from '../../ui/scrollList';
import { drawStarfield } from '../starfield';
import { getMusicService } from '../audio/MusicService';
import { hubProgress } from '../hubShared';
import { openArenaWaveDialog } from '../arenaWaveDialog';
import { textureKeyFor } from '../EnemyView';
import {
  CREATOR_ORANGE,
  CREATOR_PANEL,
  CREATOR_TEAL,
  creatorButton,
  creatorFlash,
  creatorHeader,
} from '../creator/creatorUi';

export const NO_ENEMIES_MET = 'Play some levels to meet enemies';

const ROW_H = 64;
const LIST_Y = 110;
const LIST_H = 420;

/**
 * The Wave Creator (spec 2026-11-01): the level's waves (up to 50), MAKE WAVE
 * opening the test arenas' mixer (its button reads SAVE), Peaceful, RESET
 * (two presses), − (the last wave), SAVE back to the editor.
 */
export class CreatorWavesScene extends Phaser.Scene {
  project: CreatorProject | null = null;
  private projectId = '';
  private list?: ScrollList;
  private resetArmed = false;
  private peacefulText?: Phaser.GameObjects.Text;
  private countText?: Phaser.GameObjects.Text;
  private makeBtn?: { bg: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text };
  private dialogOpen = false;

  constructor() {
    super({ key: 'CreatorWavesScene' });
  }

  init(data: { id?: string }): void {
    this.projectId = data?.id ?? this.projectId;
  }

  create(): void {
    this.project = null;
    this.resetArmed = false;
    this.dialogOpen = false;
    drawStarfield(this, 1280, 720);
    creatorHeader(this, 'WAVES', () =>
      this.scene.start('CreatorEditorScene', { id: this.projectId }),
    );
    const music = getMusicService(this);
    music.playTrack(HUB_MENU_TRACK);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      music.stop();
      this.list?.destroy();
      this.list = undefined;
    });
    void this.loadProject();
  }

  private async loadProject(): Promise<void> {
    let p: CreatorProject | undefined;
    try {
      p = (await (await creatorStoreNow()).list()).find((x) => x.id === this.projectId);
    } catch (e) {
      if (!this.sys.isActive()) return;
      creatorFlash(this, e instanceof Error ? e.message : 'Try again', 360, '#ffcf6b');
      this.time.delayedCall(1600, () => this.scene.start('CreatorMenuScene'));
      return;
    }
    if (!this.sys.isActive()) return;
    if (!p) {
      this.scene.start('CreatorMenuScene');
      return;
    }
    this.project = p;
    this.build();
  }

  private build(): void {
    this.countText = this.add
      .text(640, 92, '', { fontSize: '18px', color: '#9aa6ad', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setName('creator-wave-count');
    this.makeBtn = creatorButton(
      this,
      640,
      590,
      'MAKE WAVE',
      { name: 'creator-make-wave', width: 240, height: 56, fontSize: 24 },
      () => this.openMixer(null),
    );
    this.peacefulText = this.add
      .text(170, 560, '', { fontSize: '22px', color: '#e8f4f1', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(10)
      .setInteractive({ useHandCursor: true })
      .setName('creator-peaceful');
    this.peacefulText.on('pointerup', () => {
      if (!this.project || this.dialogOpen) return;
      this.project.peaceful = !this.project.peaceful;
      this.refresh();
    });
    const reset = creatorButton(
      this,
      170,
      630,
      'RESET',
      { name: 'creator-wave-reset', width: 160, height: 50, fill: 0xb23a3a },
      () => {
        if (!this.project || this.dialogOpen) return;
        if (!this.resetArmed) {
          this.resetArmed = true;
          reset.text.setText('SURE?');
          return;
        }
        this.resetArmed = false;
        reset.text.setText('RESET');
        this.project.waves = [];
        this.refresh();
      },
    );
    creatorButton(
      this,
      360,
      630,
      '−',
      { name: 'creator-wave-minus', width: 80, height: 50, fontSize: 30, fill: 0x5c6168 },
      () => {
        if (!this.project || this.dialogOpen) return;
        this.project.waves.pop();
        this.refresh();
      },
    );
    creatorButton(
      this,
      1150,
      670,
      'SAVE',
      { name: 'creator-waves-save', width: 180, height: 60, fontSize: 28, fill: CREATOR_ORANGE },
      () => void this.saveAndBack(),
    );
    this.refresh();
  }

  private refresh(): void {
    const p = this.project;
    if (!p) return;
    this.countText?.setText(`${p.waves.length} / ${MAX_WAVES} waves`);
    this.peacefulText?.setText(`${p.peaceful ? '☑' : '☐'}  Peaceful`);
    this.makeBtn?.bg.setAlpha(p.waves.length >= MAX_WAVES ? 0.4 : 1);
    this.list?.destroy();
    this.children.getByName('creator-waves-empty')?.destroy();
    if (p.waves.length === 0) {
      this.list = undefined;
      this.add
        .text(640, 300, 'No waves yet — press MAKE WAVE', { fontSize: '24px', color: '#9aa6ad' })
        .setOrigin(0.5)
        .setName('creator-waves-empty');
      return;
    }
    const list = new ScrollList(this, { x: 240, y: LIST_Y, width: 800, height: LIST_H, depth: 1 });
    this.list = list;
    p.waves.forEach((w, i) => {
      const y = i * (ROW_H + 6) + ROW_H / 2;
      const row = this.add
        .rectangle(400, y, 800, ROW_H, CREATOR_PANEL)
        .setStrokeStyle(2, CREATOR_TEAL)
        .setInteractive({ useHandCursor: true })
        .setName(`creator-wave-${i + 1}`);
      const label = this.add
        .text(20, y, `WAVE ${i + 1}`, { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0, 0.5);
      list.content.add([row, label]);
      w.entries.slice(0, 9).forEach((e, j) => {
        const x = 170 + j * 68;
        const key = textureKeyFor(e.enemyType);
        if (this.textures.exists(key)) {
          const icon = this.add.image(x, y - 6, key);
          icon.setScale(40 / Math.max(icon.width, icon.height, 1));
          list.content.add(icon);
        }
        list.content.add(
          this.add
            .text(x, y + 22, `×${e.count}`, {
              fontSize: '13px',
              color: '#e8f4f1',
              fontStyle: 'bold',
            })
            .setOrigin(0.5),
        );
      });
      row.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (list.dragged || !list.inView(ptr)) return;
        this.openMixer(i);
      });
    });
    list.setContentHeight(p.waves.length * (ROW_H + 6));
  }

  /** The arena mixer, SAVE-labelled: a new wave (index null) or wave `index`. */
  openMixer(index: number | null): void {
    const p = this.project;
    if (!p || this.dialogOpen) return;
    if (index === null && p.waves.length >= MAX_WAVES) {
      creatorFlash(this, `A level can have ${MAX_WAVES} waves at most`);
      return;
    }
    const types = metEnemyTypes(hubProgress().enteredLevels());
    if (types.length === 0) {
      creatorFlash(this, NO_ENEMIES_MET);
      return;
    }
    const existing = index === null ? undefined : p.waves[index];
    const opened = openArenaWaveDialog(this, {
      types,
      friendly: true,
      bossToggle: true,
      submitLabel: 'SAVE',
      maxEntries: MAX_WAVE_ENTRIES,
      initial: existing,
      onStart: (entries, interval, mods) => {
        const wave: CreatorWave = { entries, interval, mods: compactMods(mods) };
        if (index === null) p.waves.push(wave);
        else p.waves[index] = wave;
        this.refresh();
      },
      onClose: () => {
        this.dialogOpen = false;
      },
    });
    this.dialogOpen = opened;
  }

  private async saveAndBack(): Promise<void> {
    if (!this.project || this.dialogOpen) return;
    try {
      await (await creatorStoreNow()).save(this.project);
      if (this.sys.isActive()) this.scene.start('CreatorEditorScene', { id: this.projectId });
    } catch (e) {
      if (this.sys.isActive())
        creatorFlash(this, e instanceof Error ? e.message : 'Could not save');
    }
  }
}

/** Only the toggles that are ON — the saved wave stays small. */
function compactMods(m: object): CreatorWave['mods'] {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(m)) if (v === true) out[k] = true;
  return out;
}
